import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import BannerComponent from "../components/bannerComponent/bannerComponent";
import ButtonLinkComponent from "../components/buttonLink/buttonLink";
import "../style/contact-us.css";

// El backend (API + panel de Talento Humano) vive en panel.5igsolutions.com,
// un dominio aparte del sitio publico (5igsolutions.com). En local, donde el
// mismo servidor Express sirve tanto la API como el sitio, esta variable
// queda vacia y las llamadas siguen siendo relativas (mismo origen).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
  });

const Work_with_us = () => {
  const { t, i18n } = useTranslation();
  const formRef = useRef(null);
  const formElRef = useRef(null);
  const [submitStatus, setSubmitStatus] = useState("idle"); // idle | submitting | success | error
  const [formError, setFormError] = useState("");
  // El formulario no vive siempre visible en la pagina: solo aparece cuando
  // alguien hace clic en "Aplicar" a una vacante, o llega por un link
  // copiado del panel ("?vacante=<id>"), y vuelve a ocultarse despues de
  // enviarse — a pedido de Lorena.
  const [showForm, setShowForm] = useState(false);
  const hideFormTimeoutRef = useRef(null);

  const [vacancies, setVacancies] = useState([]);
  const featuredVacancies = vacancies.filter((job) => job.is_featured);

  const JOBS_PER_PAGE = 5;
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [allJobsPage, setAllJobsPage] = useState(1);
  const totalJobsPages = Math.max(1, Math.ceil(vacancies.length / JOBS_PER_PAGE));
  const paginatedVacancies = vacancies.slice(
    (allJobsPage - 1) * JOBS_PER_PAGE,
    allJobsPage * JOBS_PER_PAGE
  );

  const toggleAllJobs = () => {
    setShowAllJobs((prev) => !prev);
    setAllJobsPage(1);
  };

  useEffect(() => {
    let cancelled = false;

    // Las vacantes las escribe Talento Humano en español; si el sitio esta
    // en ingles, se traducen al vuelo en el backend (ver server/translate.js)
    // en vez de mostrarlas siempre en español.
    const lang = i18n.language && i18n.language.startsWith("en") ? "en" : "es";

    fetch(`${API_BASE_URL}/api/vacancies?lang=${lang}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("load_failed"))))
      .then((data) => {
        if (!cancelled) setVacancies(data.vacancies || []);
      })
      .catch((err) => {
        console.error("Error cargando vacantes:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const benefits = [
    {
      title: t("WORK_WITH_US_CONTENT_HIGH_PERFORMANCE_TEAM"),
      text: t("WORK_WITH_US_BENEFIT_0_DESC"),
      icon: "/img/succesIcon.svg",
      featured: true,
    },
    {
      title: t("WORK_WITH_US_CONTENT_PSYCHOLOGICAL_SUPPORT"),
      text: t( "WORK_WITH_US_BENEFIT_1_DESC"),
      icon: "/img/psychologicalSupportIcon.svg",
      featured: true,
    },
    {
      title: t("WORK_WITH_US_CONTENT_OPHTHALMOLOGICAL_AID"),
      text: t("WORK_WITH_US_BENEFIT_2_DESC"),
      icon: "/img/ophthalmologyIcon.svg",
      featured: true,
    },
    {
      title: t("WORK_WITH_US_CONTENT_LIFE_INSURANCE"),
      text: t( "WORK_WITH_US_BENEFIT_3_DESC"),
      icon: "/img/lifeInsuranceIcon.svg",
      featured: true,
    },
    {
      title: t("WORK_WITH_US_CONTENT_EDUCATIONAL_AID"),
      text: t("WORK_WITH_US_BENEFIT_5_DESC"),
      icon: "/img/educationIcon.svg",
    },
    {
      title: t("WORK_WITH_US_CONTENT_CONNECTIVITY_ALLOWANCE"),
      text: t("WORK_WITH_US_BENEFIT_6_DESC"),
      icon: "/img/connectivityIcon.svg",
    },
  ];

  const [selectedVacancy, setSelectedVacancy] = useState("");

  const handleApplyClick = (vacancyTitle, scrollBehavior = "smooth") => {
    if (hideFormTimeoutRef.current) {
      clearTimeout(hideFormTimeoutRef.current);
      hideFormTimeoutRef.current = null;
    }
    setSubmitStatus("idle");
    setFormError("");
    setSelectedVacancy(vacancyTitle);
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: scrollBehavior, block: "start" });
    }, 80);
  };

  useEffect(() => {
    return () => {
      if (hideFormTimeoutRef.current) clearTimeout(hideFormTimeoutRef.current);
    };
  }, []);

  // Enlaces copiados desde el panel de vacantes traen "?vacante=<id>": en
  // cuanto cargan las vacantes, si el id coincide con una activa, se
  // preselecciona y se muestra el formulario directamente, igual que si la
  // persona hubiera hecho clic en "Aplicar" — pero sin la animacion de
  // scroll (llega por un link, no tiene nada que "recorrer" en la pagina
  // primero: el formulario debe verse de una vez). Solo una vez por visita.
  const autoAppliedFromUrlRef = useRef(false);

  useEffect(() => {
    if (autoAppliedFromUrlRef.current || vacancies.length === 0) return;

    const vacancyId = new URLSearchParams(window.location.search).get("vacante");
    if (!vacancyId) return;

    const match = vacancies.find((job) => String(job.id) === vacancyId);
    if (match) {
      autoAppliedFromUrlRef.current = true;
      handleApplyClick(match.title, "auto");
    }
  }, [vacancies]);

  // Los campos de link (LinkedIn, CV, video) se piden como texto
  // libre en vez de type="url" — ese tipo de input exige que la persona
  // escriba "https://" a mano o el navegador rechaza el valor, lo cual
  // confunde a quien solo pega "linkedin.com/in/usuario". Acá se completa el
  // protocolo automaticamente si falta.
  const normalizeUrl = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = formElRef.current;
    const formData = new FormData(form);
    const resumeFile = formData.get("resume");
    const resumeUrl = normalizeUrl(formData.get("resumeUrl"));
    const hasResumeFile = resumeFile && resumeFile.size > 0;

    // El CV se puede adjuntar como archivo o pegar como link (Drive,
    // Dropbox, etc.) — se necesita al menos uno de los dos.
    if (!hasResumeFile && !resumeUrl) {
      setFormError(t("WORK_US_RESUME_REQUIRED_ERROR", "Adjunta tu CV o pega un link."));
      setSubmitStatus("error");
      return;
    }

    setFormError("");
    setSubmitStatus("submitting");

    try {
      const payload = {
        firstName: formData.get("firstName"),
        lastName: formData.get("lastName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        country: formData.get("country"),
        position: formData.get("position"),
        experience: formData.get("experience"),
        portfolio: normalizeUrl(formData.get("portfolio")),
        message: formData.get("message"),
        videoUrl: normalizeUrl(formData.get("videoUrl")),
        resumeUrl: resumeUrl || null,
        privacy: formData.get("privacy") === "on",
        resume: hasResumeFile
          ? {
              filename: resumeFile.name,
              contentType: resumeFile.type,
              content: await fileToBase64(resumeFile),
            }
          : null,
      };

      const response = await fetch(`${API_BASE_URL}/api/work-with-us`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("request_failed");

      setSubmitStatus("success");
      form.reset();
      setSelectedVacancy("");

      // El formulario no se queda visible despues de enviarse: se ve la
      // confirmacion un momento y despues desaparece, en vez de quedar como
      // una seccion fija de la pagina.
      hideFormTimeoutRef.current = setTimeout(() => {
        setShowForm(false);
        setSubmitStatus("idle");
      }, 4500);
    } catch (err) {
      setSubmitStatus("error");
    }
  };

  return (
    <>
      <Header />

      <main className="wwuPage">
        <section className="wwuHero wwuHeroFull">
          <div className="wwuHeroBannerFull">
            <BannerComponent
              translateTitle={t("WORK_WITH_US_HERO_TITLE")}
              translateText={t("WORK_WITH_US_HERO_SUBTITLE")}
              className="wwuHeroBanner"
              classNameSide="l"
              translateButton={t("WORK_WITH_US_HERO_CTA")}
              chipText={t("WORK_WITH_US")}
              onClick={() => handleApplyClick("")}
              headingTag="h1"
            />
          </div>
        </section>

        <section className="wwuIntro">
          <div className="wwuSectionIntro">
            <h2>{t("WORK_WITH_US_INTRO_TITLE")}</h2>
            <p>{t("WORK_WITH_US_INTRO_DESC")}
            </p>
          </div>
        </section>

        <section className="wwuJobs">
          <div className="wwuBlockWrap">
            <div className="wwuSectionIntro">
              <h2>{t("WORK_WITH_US_FEATURED_TITLE")}</h2>
              <p> {t("WORK_WITH_US_FEATURED_DESC" )}</p>
            </div>

            {featuredVacancies.length === 0 ? (
              <p className="wwuEmptyState">
                {t("WORK_WITH_US_NO_FEATURED_JOBS", "No hay vacantes destacadas en este momento.")}
              </p>
            ) : (
              <div className={`wwuJobsGrid${featuredVacancies.length < 3 ? " wwuJobsGrid--center" : ""}`}>
                {featuredVacancies.map((job) => (
                  <article className="wwuJobCard" key={job.id}>
                    <div className="wwuJobMeta">
                      <span>{job.type}</span>
                      <span>{job.location}</span>
                    </div>

                    <h3>{job.title}</h3>

                    {job.description && <p className="wwuJobDesc">{job.description}</p>}

                    <button
                      type="button"
                      className="wwuJobButton"
                      onClick={() => handleApplyClick(job.title)}
                    >
                      {t("WORK_WITH_US_APPLY")}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="wwuAllJobs">
          <div className="wwuBlockWrap">
            <div className="wwuSectionIntro">
              <h2>{t("WORK_WITH_US_ALL_JOBS_TITLE", "Todas las vacantes")}</h2>
              <p>{t("WORK_WITH_US_ALL_JOBS_DESC", "Explora el listado completo de posiciones abiertas en 5IG Solutions.")}</p>
            </div>

            <div className="wwuAllJobsToggleWrap">
              <button type="button" className="wwuAllJobsToggleBtn" onClick={toggleAllJobs}>
                {showAllJobs
                  ? t("WORK_WITH_US_HIDE_JOBS", "Ocultar vacantes")
                  : t("WORK_WITH_US_SHOW_JOBS", "Mostrar todas las vacantes")}
              </button>
            </div>

            {showAllJobs && (
              vacancies.length === 0 ? (
                <p className="wwuEmptyState">
                  {t("WORK_WITH_US_NO_JOBS", "No hay vacantes disponibles en este momento.")}
                </p>
              ) : (
                <>
                  <div className="wwuAllJobsList">
                    {paginatedVacancies.map((job) => (
                      <div className="wwuJobRow" key={job.id}>
                        <div className="wwuJobRowInfo">
                          <h3>{job.title}</h3>
                          <div className="wwuJobMeta">
                            <span>{job.type}</span>
                            <span>{job.location}</span>
                          </div>
                          {job.description && <p className="wwuJobDesc">{job.description}</p>}
                        </div>

                        <button
                          type="button"
                          className="wwuJobRowBtn"
                          onClick={() => handleApplyClick(job.title)}
                        >
                          {t("WORK_WITH_US_APPLY")}
                        </button>
                      </div>
                    ))}
                  </div>

                  {totalJobsPages > 1 && (
                    <div className="wwuJobsPagination">
                      <button
                        type="button"
                        className="wwuJobsPaginationBtn"
                        disabled={allJobsPage === 1}
                        onClick={() => setAllJobsPage((p) => Math.max(1, p - 1))}
                      >
                        {t("WORK_WITH_US_PREV_PAGE", "Anterior")}
                      </button>

                      <span className="wwuJobsPaginationInfo">
                        {t("WORK_WITH_US_PAGE_LABEL", "Página")} {allJobsPage} {t("WORK_WITH_US_PAGE_OF", "de")} {totalJobsPages}
                      </span>

                      <button
                        type="button"
                        className="wwuJobsPaginationBtn"
                        disabled={allJobsPage === totalJobsPages}
                        onClick={() => setAllJobsPage((p) => Math.min(totalJobsPages, p + 1))}
                      >
                        {t("WORK_WITH_US_NEXT_PAGE", "Siguiente")}
                      </button>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </section>

        <section className="wwuBenefits">
          <div className="wwuBlockWrap">
            <div className="wwuSectionIntro">
              <h2>{t("WORK_WITH_US_BENEFITS_TITLE")}</h2>
              <p>{t("WORK_WITH_US_BENEFITS_DESC")}</p>
            </div>

            <div className="wwuBenefitsGrid">
              {benefits.map((item, index) => (
                <article className={`wwuBenefitCard ${item.featured ? "featured" : ""}`} key={index}>
                  <div className="wwuBenefitIcon">
                    <img src={item.icon} alt="" />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {showForm && (
        <section className="wwuFormSection" ref={formRef}>
          <div className="wwuFormWrap">
            <div className="wwuSectionIntro">
              <h2>{t("WORK_WITH_US_FORM_TITLE")}</h2>
              <p> {t("WORK_WITH_US_FORM_DESC" )} </p>
            </div>

            <form
              className="wwuForm"
              ref={formElRef}
              onSubmit={handleSubmit}
            >
              <div className="wwuFormGrid">
                <div className="wwuField">
                  <label htmlFor="firstName">{t("FORM_FIRST_NAME")}</label>
                  <input id="firstName" name="firstName" type="text" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="lastName">{t("FORM_LAST_NAME")}</label>
                  <input id="lastName" name="lastName" type="text" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="email">{t("FORM_EMAIL")}</label>
                  <input id="email" name="email" type="email" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="phone">{t("FORM_PHONE_NUMBER")}</label>
                  <input id="phone" name="phone" type="tel" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="country">{t("FORM_LABEL_COUNTRY")}</label>
                  <input id="country" name="country" type="text" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="position">{t("FORM_LABEL_JOB_POSITION")}</label>
                  <input
                    id="position"
                    name="position"
                    type="text"
                    value={selectedVacancy}
                    onChange={(e) => setSelectedVacancy(e.target.value)}
                    required
                  />
                </div>

                <div className="wwuField">
                  <label htmlFor="experience">{t("FORM_LABEL_PREVIOUS_EXPERIENCE")}</label>
                  <input id="experience" name="experience" type="number" min="0" required />
                </div>

                <div className="wwuField">
                  <label htmlFor="portfolio">{t("WORK_WITH_US_PORTFOLIO", "LinkedIn")}</label>
                  <input id="portfolio" name="portfolio" type="text" placeholder="linkedin.com/in/tu-usuario" required />
                </div>

                <div className="wwuField wwuFieldFull">
                  <label htmlFor="resume">{t("FORM_LABEL_SUBMIT_RESUME")}</label>
                  <input
                    id="resume"
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                  />
                  <small>{t("WORK_WITH_US_FILE_HELP")}</small>
                </div>

                <div className="wwuField wwuFieldFull">
                  <label htmlFor="resumeUrl">{t("WORK_US_RESUME_LINK_LABEL")}</label>
                  <input id="resumeUrl" name="resumeUrl" type="text" placeholder="drive.google.com/..." />
                  <small>{t("WORK_US_RESUME_LINK_HELP")}</small>
                </div>

                <div className="wwuField wwuFieldFull">
                  <label htmlFor="message">{t("WORK_US_RESUME_MESSAGE")}</label>
                  <textarea id="message" name="message" rows="5" required />
                </div>

                <div className="wwuField wwuFieldFull">
                  <label htmlFor="videoUrl">{t("WORK_US_VIDEO_LABEL")}</label>
                  <input
                    id="videoUrl"
                    name="videoUrl"
                    type="text"
                    placeholder="youtube.com/... o drive.google.com/..."
                    required
                  />
                  <small>{t("WORK_US_VIDEO_HELP")}</small>
                </div>
              </div>

              <label className="wwuCheckbox">
                <input type="checkbox" name="privacy" required />
                <span>
                  {t("FORM_POLICY")}{" "}
                  <a href="/PoliticaDeTratamientoDeDatosPersonales.pdf" target="_blank" rel="noreferrer">
                    {t("FORM_POLICY_LINK")}
                  </a>
                </span>
              </label>

              <button type="submit" className="wwuSubmitBtn" disabled={submitStatus === "submitting"}>
                {submitStatus === "submitting" ? t("FORM_BUTTON_LOADING", "Enviando...") : t("FORM_BUTTON")}
              </button>

              {submitStatus === "success" && (
                <p style={{ color: "#2f7d4f", marginTop: "12px" }}>
                  {t("FORM_SUCCESS_MSG", "¡Aplicación enviada con éxito! Revisa tu correo, te acabamos de escribir. Nos pondremos en contacto pronto.")}
                </p>
              )}
              {submitStatus === "error" && (
                <p style={{ color: "#b1382c", marginTop: "12px" }}>
                  {formError || t("ERROR_MSG", "Ocurrió un error. Por favor intenta de nuevo.")}
                </p>
              )}
            </form>
          </div>
        </section>
        )}

        <section className="wwuCta">
          <div className="wwuCtaInner">
            <div className="wwuCtaContent">
              <h2>{t("WORK_WITH_US_CTA_TITLE")}</h2>
              <p>{t( "WORK_WITH_US_CTA_DESC")}</p>
            </div>

            <ButtonLinkComponent
              translateButton={t("WORK_WITH_US_CTA_BUTTON")}
              url={"mailto:recursos.humanos@5igsolutions.com?subject=Job%20Application%20-%205IG%20Solutions"}
              blank={false}
            />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Work_with_us;